/**
 * ClientsTable — таблица клиентов (десктоп) по макету. Богатые ячейки
 * (тип-иконка, тариф с подстрокой, статус-пилюля, дата продления с тоном),
 * клик по строке → открыть карточку клиента (drawer — следующая задача).
 */

import {ChevronRight} from 'lucide-react';

import {type ClientListItem} from '@services/clients';
import {StatusPill} from '@components/StatusPill';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@components/ui/Table';
import {formatNumber} from '@utils/format';

import {renewalMeta, renewalToneClass, tariffSub, typeLabel} from '../Clients.utils';
import {ClientTypeIcon} from '../ClientTypeIcon';

const th = 'text-[11px] font-semibold uppercase tracking-[.04em] text-skr-label';

type ClientsTableProps = {
  rows: ClientListItem[];
  onOpenClient: (id: string) => void;
};

export const ClientsTable = ({rows, onOpenClient}: ClientsTableProps) => (
  <div className="overflow-x-auto">
    <Table className="min-w-[940px] text-[13.5px]">
      <TableHeader>
        <TableRow className="border-b border-skr-line2 bg-skr-head hover:bg-skr-head">
          <TableHead className={th}>Клиент</TableHead>
          <TableHead className={th}>Город</TableHead>
          <TableHead className={th}>Тариф</TableHead>
          <TableHead className={`${th} text-right`}>Пациенты</TableHead>
          <TableHead className={`${th} text-right`}>Выручка, сом</TableHead>
          <TableHead className={th}>Статус</TableHead>
          <TableHead className={th}>Продление</TableHead>
          <TableHead className="w-9" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((c) => {
          const renew = renewalMeta(c.currentPeriodEnd);
          return (
            <TableRow
              key={c.id}
              onClick={() => onOpenClient(c.id)}
              className="cursor-pointer border-b border-skr-hairline hover:bg-skr-head"
            >
              <TableCell>
                <div className="flex items-center gap-3">
                  <ClientTypeIcon type={c.type} size={36} />
                  <div className="leading-[1.35]">
                    <div className="font-semibold text-foreground">{c.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {typeLabel(c.type)} · <span className="font-mono">{c.adminPhone}</span>
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-skr-ink2">{c.cityName}</TableCell>
              <TableCell>
                <div className="text-skr-ink">{typeLabel(c.type)}</div>
                <div
                  className={`text-xs text-muted-foreground${c.type === 'clinic' ? ' font-mono' : ''}`}
                >
                  {tariffSub(c)}
                </div>
              </TableCell>
              <TableCell
                className={`tnum text-right font-mono ${c.patientsCount === 0 ? 'text-skr-faint' : 'text-skr-ink'}`}
              >
                {formatNumber(c.patientsCount)}
              </TableCell>
              <TableCell
                className={`tnum text-right font-mono font-medium ${c.revenue === 0 ? 'text-skr-faint' : 'text-foreground'}`}
              >
                {formatNumber(c.revenue)}
              </TableCell>
              <TableCell>
                <StatusPill status={c.subscriptionStatus} trialDay={c.trialDay} />
              </TableCell>
              <TableCell className={`font-mono text-[13px] ${renewalToneClass(renew.tone)}`}>
                {renew.text}
              </TableCell>
              <TableCell className="pr-2">
                <ChevronRight className="size-4 text-skr-chevron" strokeWidth={2} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);
